// src/app/core/http/error.interceptor.ts
import type {
  HttpInterceptorFn, HttpHandlerFn, HttpRequest,
  HttpErrorResponse
} from '@angular/common/http';
import { inject } from '@angular/core';
import { NEVER, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthService } from '../auth/auth.service';
import { SessionService } from '../auth/session.service';
import { LoggerService } from '../logging/logger.service';

/**
 * ErrorInterceptor — manejo centralizado de errores HTTP.
 *
 * Responsabilidades por código de error:
 * - 400 → DEJAR PASAR al componente (contiene validaciones del servidor)
 * - 401 → logout + navegar a /login
 * - 408 → mostrar error de timeout (generado por TimeoutInterceptor)
 * - 429 → leer header Retry-After + mostrar error con tiempo de espera
 * - 500 → error de servidor
 * - 502/503/504 → servicio no disponible
 * - 0   → verificar navigator.onLine → offline vs error de red
 *
 * IMPORTANTE: los errores 400 pasan al componente para que los muestre
 * en contexto (validaciones de formulario, reglas de negocio, etc.)
 */
export const errorInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {
  const router  = inject(Router);
  const auth    = inject(AuthService);
  const session = inject(SessionService);
  const logger  = inject(LoggerService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // ── Sesión ya cerrada (D9, §1.5 punto 3) ────────────────────────────
      // Un error que llega DESPUÉS de que SessionService.logout() ya cortó
      // el tráfico (sessionCancelInterceptor) o cerró la sesión por
      // cualquier otra vía es ruido, no una condición real que el usuario
      // deba ver: no puede convertirse en un toast rojo sobre la pantalla
      // de login, ni en un reintento con credenciales ya borradas, ni en un
      // segundo logout() reentrante. NEVER — ni next, ni error, ni complete
      // — porque el llamador original ya fue destruido o está navegando
      // fuera; no hay nadie a quien avisar de éxito ni de fallo.
      if (session.state() === 'expired') {
        return NEVER;
      }

      // ── 400 Bad Request ─────────────────────────────────────────────────
      // Dejar pasar al componente — contiene validaciones del servidor
      // que el componente debe mostrar en contexto (formularios, etc.)
      if (error.status === 400) {
        return throwError(() => error);
      }

      // ── 401 Unauthorized ─────────────────────────────────────────────────
      // Sesión expirada o token inválido → logout + redirección
      if (error.status === 401) {
        logger.warn('ErrorInterceptor', '401 recibido — cerrando sesión');
        auth.logout();
        router.navigate(['/login']);
        return throwError(() => error);
      }

      // ── 408 Request Timeout ───────────────────────────────────────────────
      // Generado por TimeoutInterceptor cuando la petición supera timeoutMs
      if (error.status === 408) {
        logger.warn('ErrorInterceptor', `Timeout en ${req.method} ${req.url}`);
        return throwError(() => ({
          ...error,
          userMessage: 'common.errors.timeout',
        }));
      }

      // ── 429 Too Many Requests ─────────────────────────────────────────────
      // Leer Retry-After si existe para mostrar tiempo de espera real
      if (error.status === 429) {
        const retryAfter = error.headers?.get('Retry-After');
        const waitSeconds = retryAfter ? parseInt(retryAfter, 10) : null;
        logger.warn('ErrorInterceptor', `429 Too Many Requests. Retry-After: ${retryAfter ?? 'no especificado'}`);
        return throwError(() => ({
          ...error,
          userMessage: 'common.errors.tooManyRequests',
          retryAfterSeconds: waitSeconds,
        }));
      }

      // ── 0 Error de red ────────────────────────────────────────────────────
      // status 0 = sin conexión, CORS, o servidor no alcanzable
      if (error.status === 0) {
        const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;
        const userMessage = isOffline
          ? 'common.errors.offline'
          : 'common.errors.networkError';
        logger.warn('ErrorInterceptor', `Error de red (status 0). Online: ${navigator.onLine}`);
        return throwError(() => ({
          ...error,
          userMessage,
        }));
      }

      // ── 500 Internal Server Error ─────────────────────────────────────────
      if (error.status === 500) {
        logger.error('ErrorInterceptor', error, { url: req.url, method: req.method });
        return throwError(() => ({
          ...error,
          userMessage: 'common.errors.serverError',
        }));
      }

      // ── 502/503/504 Service Unavailable ──────────────────────────────────
      if (error.status === 502 || error.status === 503 || error.status === 504) {
        logger.warn('ErrorInterceptor', `Servicio no disponible: ${error.status}`);
        return throwError(() => ({
          ...error,
          userMessage: 'common.errors.serviceUnavailable',
        }));
      }

      // ── Otros errores ─────────────────────────────────────────────────────
      logger.error('ErrorInterceptor', error, {
        url:    req.url,
        method: req.method,
        status: String(error.status),
      });
      return throwError(() => ({
        ...error,
        userMessage: 'common.errors.generic',
      }));
    })
  );
};
